import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { TriangleAlert } from 'lucide-react';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import SingleSelectInput from '@/components/form/SingleSelectInput.tsx';
import Dot from '@/components/ui/Dot.tsx';
import { InfoCardRow } from '@/components/ui/InfoCard.tsx';
import useGetCostCategories from '@/features/dashboard/costCategories/hooks/useGetCostCategories.ts';
import useGetCostCategoryUsage from '@/features/dashboard/costCategories/hooks/useGetCostCategoryUsage.ts';
import useMergeCostCategory from '@/features/dashboard/costCategories/hooks/useMergeCostCategory.ts';
import { getCostCategoryMergeMessage } from '@/features/dashboard/costCategories/api/costCategoryErrors.ts';
import { CostCategoryDto } from '@/features/dashboard/costCategories/types/CostCategoryDto.ts';
import { pluralize } from '@/utils/pluralize.ts';

const mergeSchema = z.object({
  targetId: z.string().min(1, 'Wybierz kategorię docelową'),
});

type MergeFormData = z.infer<typeof mergeSchema>;

interface MergeCostCategoryModalProps {
  category: CostCategoryDto;
  onClose: () => void;
}

const MergeCostCategoryModal = ({
  category,
  onClose,
}: MergeCostCategoryModalProps) => {
  // Zarchiwizowanej kategorii backend nie przyjmie jako celu (`COST_CATEGORY_ARCHIVED`).
  const { data: categories } = useGetCostCategories(false);
  const { data: usage } = useGetCostCategoryUsage(category.id);
  const mergeMutation = useMergeCostCategory();

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isDirty },
  } = useForm<MergeFormData>({
    resolver: zodResolver(mergeSchema),
    defaultValues: { targetId: '' },
  });

  // `useWatch` zamiast `watch()` — to drugie wyłącza kompilator React w całym komponencie.
  const targetId = useWatch({ control, name: 'targetId' });

  const targets = (categories ?? [])
    .filter((item) => item.id !== category.id)
    .sort((a, b) => a.name.localeCompare(b.name, 'pl'));

  const options = targets.map((item) => ({
    value: item.id,
    name: item.name,
    label: (
      <span className="flex items-center gap-2">
        <Dot size="xl" hollow style={{ color: item.color }} />
        <span>{item.name}</span>
      </span>
    ),
  }));
  const targetNames = new Map(targets.map((item) => [item.id, item.name]));
  const targetName = targetNames.get(targetId) ?? 'wybranej kategorii';

  const onSubmit = (data: MergeFormData) =>
    mergeMutation.mutate(
      { id: category.id, targetId: data.targetId },
      {
        onSuccess: () => onClose(),
        onError: (error) => {
          const message = getCostCategoryMergeMessage(error);
          if (message) setError('targetId', { message });
        },
      },
    );

  return (
    <Modal onClose={onClose} isDirty={isDirty}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader
          title="Scal kategorię"
          description={`Kategorie kosztów · ${category.name}`}
          onClose={onClose}
        />
        <ModalContent>
          <div className="rounded-md bg-neutral-50 p-3">
            <InfoCardRow layout="columns" label="Źródło">
              <span className="flex items-center gap-2">
                <Dot size="xl" hollow style={{ color: category.color }} />
                <span>{category.name}</span>
                {usage && (
                  <span className="font-normal text-neutral-400">
                    {`· ${pluralize(usage.expenseCount, 'wydatek', 'wydatki', 'wydatków')}`}
                  </span>
                )}
              </span>
            </InfoCardRow>
          </div>

          <SingleSelectInput
            fieldName="targetId"
            control={control}
            options={options}
            label="Kategoria docelowa"
            required
            error={errors.targetId?.message}
            hint="Lista nie zawiera kategorii zarchiwizowanych ani tej samej kategorii"
            placeholder="Wybierz kategorię..."
          />

          <div className="flex flex-row items-start gap-2.5 rounded-[10px] bg-accent-100 p-3">
            <TriangleAlert
              className="mt-px size-4 shrink-0 text-accent-900"
              aria-hidden
            />
            <p className="text-[13px] leading-[18px] text-accent-900">
              {`Wydatki z kategorii ${category.name} przejdą do ${targetName}, a ${category.name} zniknie z listy. Historia i raporty pokażą je już pod nową nazwą.`}
            </p>
          </div>
        </ModalContent>
        <ModalFooter>
          <ActionButton tone="ghost" size="sm" type="button" onClick={onClose}>
            Anuluj
          </ActionButton>
          <ActionButton
            tone="danger"
            size="sm"
            type="submit"
            isLoading={mergeMutation.isPending}
          >
            Scal kategorie
          </ActionButton>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default MergeCostCategoryModal;
